import React, { useState, useEffect } from "react";
import { FolderPlus, Upload, X, Folder, File } from "lucide-react";
import { toast } from "react-toastify";

const UploadResourceModal = ({ isOpen, onClose, batchNo, onUpload }) => {
  const [uploadMode, setUploadMode] = useState("file");
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && batchNo) {
      loadModules();
      setUploadMode("file");
      setSelectedModule("");
      setNewFolderName("");
      setFiles([]);
    }
  }, [isOpen, batchNo]);

  const loadModules = async () => {
    try {
      const res = await fetch(
        `https://trainer-portal-78f5.onrender.com/api/lms/folders?batchNo=${batchNo}`,
      );
      const data = await res.json();
      if (data.success) setModules(data.folders || []);
    } catch (error) {
      console.error("Error loading modules:", error);
      toast.error("Failed to load folders");
    }
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const uploadFiles = async () => {
    if (uploadMode === "file" && !selectedModule) {
      toast.warning("Please select a folder");
      return;
    }
    if (uploadMode === "folder" && !newFolderName.trim()) {
      toast.warning("Please enter a folder name");
      return;
    }
    if (!files.length) {
      toast.warning("Please select at least one file");
      return;
    }

    const folderName =
      uploadMode === "file" ? selectedModule : newFolderName.trim();

    const formData = new FormData();
    formData.append("batchNo", batchNo);
    formData.append("folderName", folderName);
    files.forEach((f) => {
      formData.append("files", f);
    });

    setLoading(true);

    try {
      const res = await fetch(
        "https://trainer-portal-78f5.onrender.com/api/lms/upload-resource",
        {
          method: "POST",
          body: formData,
        },
      );

      const data = await res.json();

      if (data.success) {
        toast.success("File uploaded successfully!");
        onUpload?.();
        onClose();
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              Upload Resources
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Batch #{batchNo}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Upload Mode selection */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
              Select Upload Mode
            </label>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Upload File Option */}
              <label className="flex-1 cursor-pointer">
                <div
                  className={`flex items-center p-3 sm:p-4 border-2 rounded-lg transition-all ${
                    uploadMode === "file"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="uploadMode"
                    value="file"
                    checked={uploadMode === "file"}
                    onChange={(e) => setUploadMode(e.target.value)}
                    className="w-4 h-4 text-blue-600 flex-shrink-0"
                  />
                  <Upload className="w-4 h-4 sm:w-5 sm:h-5 ml-2 sm:ml-3 mr-2 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm sm:text-base text-gray-900">
                      Upload File
                    </div>
                    <div className="text-xs text-gray-500">
                      Upload to existing folder
                    </div>
                  </div>
                </div>
              </label>

              {/* Create Folder Option */}
              <label className="flex-1 cursor-pointer">
                <div
                  className={`flex items-center p-3 sm:p-4 border-2 rounded-lg transition-all ${
                    uploadMode === "folder"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="uploadMode"
                    value="folder"
                    checked={uploadMode === "folder"}
                    onChange={(e) => setUploadMode(e.target.value)}
                    className="w-4 h-4 text-green-600 flex-shrink-0"
                  />
                  <FolderPlus className="w-4 h-4 sm:w-5 sm:h-5 ml-2 sm:ml-3 mr-2 text-green-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm sm:text-base text-gray-900">
                      Create Folder
                    </div>
                    <div className="text-xs text-gray-500">
                      Create new folder
                    </div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Existing folder select */}
          {uploadMode === "file" && (
            <div className="mb-4 sm:mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Folder <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Folder className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none" />
                <select
                  className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white text-sm sm:text-base"
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                >
                  <option value="">-- Select a folder --</option>
                  {modules.map((mod) => (
                    <option key={mod} value={mod}>
                      {mod}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* New Folder */}
          {uploadMode === "folder" && (
            <div className="mb-4 sm:mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Folder Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FolderPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="e.g., Module 1, Week 1"
                  className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* File picker */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Choose File <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center hover:border-gray-400 transition-colors">
              <File className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 mx-auto mb-2 sm:mb-3" />
              <input
                type="file"
                multiple
                className="hidden"
                id="fileInput"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.sql,.zip,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.avi,.mov,.csv,.json,.xml,.html,.css,.js,.java,.py,.cpp,.c,.avif,.ipynb"
              />

              <label
                htmlFor="fileInput"
                className="cursor-pointer text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Click to browse files
              </label>
              <p className="text-xs text-gray-500 mt-1">
                You can select multiple files
              </p>
            </div>

            {files.length > 0 && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                <p className="font-semibold mb-1 text-xs sm:text-sm">
                  Selected files ({files.length}):
                </p>
                <ul className="text-xs max-h-32 overflow-y-auto space-y-1">
                  {files.map((f, i) => (
                    <li key={i} className="truncate">
                      • {f.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-auto px-4 sm:px-5 py-2.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            onClick={uploadFiles}
            disabled={loading}
            className="w-full sm:w-auto px-4 sm:px-5 py-2.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Uploading..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadResourceModal;
